import { useEffect, useState } from "react";
import { StreamChat } from "stream-chat";
import api from "../../utils/api";
import { apiUrls } from "../../utils/urls";

let sharedClient = null;
let sharedUserId = null;
let sharedStreamUser = null;
let sharedIsEmployee = false;
let sharedInitializationPromise = null;
let activeHookConsumers = 0;
let pendingDisconnectTimeout = null;

async function connectSharedClient(apiKey, user, token) {
  if (pendingDisconnectTimeout) {
    clearTimeout(pendingDisconnectTimeout);
    pendingDisconnectTimeout = null;
  }

  const client = sharedClient || StreamChat.getInstance(apiKey);
  const targetUserId = String(user.id);

  if (client.userID && String(client.userID) !== targetUserId) {
    await client.disconnectUser();
  }

  if (String(client.userID || "") !== targetUserId) {
    await client.connectUser(user, token);
  }

  sharedClient = client;
  sharedUserId = targetUserId;
  return client;
}

function scheduleSharedDisconnectIfUnused() {
  if (!sharedClient || activeHookConsumers > 0) {
    return;
  }

  pendingDisconnectTimeout = setTimeout(async () => {
    if (!sharedClient || activeHookConsumers > 0) {
      return;
    }
    try {
      await sharedClient.disconnectUser();
    } finally {
      sharedClient = null;
      sharedUserId = null;
      sharedStreamUser = null;
      sharedIsEmployee = false;
      pendingDisconnectTimeout = null;
    }
  }, 1500);
}

async function initializeSharedClient() {
  if (sharedClient && sharedStreamUser) {
    return {
      client: sharedClient,
      streamUser: sharedStreamUser,
      isEmployee: sharedIsEmployee,
    };
  }

  if (sharedInitializationPromise) {
    return sharedInitializationPromise;
  }

  sharedInitializationPromise = (async () => {
    const response = await api.get(apiUrls.messaging.token());
    const data = response.data;
    const chatClient = await connectSharedClient(data.api_key, data.user, data.token);

    sharedClient = chatClient;
    sharedUserId = String(data.user.id);
    sharedStreamUser = data.user;
    sharedIsEmployee = Boolean(data.is_employee);

    return {
      client: chatClient,
      streamUser: data.user,
      isEmployee: Boolean(data.is_employee),
    };
  })();

  try {
    return await sharedInitializationPromise;
  } finally {
    sharedInitializationPromise = null;
  }
}

export default function useStreamClient() {
  const [client, setClient] = useState(null);
  const [streamUser, setStreamUser] = useState(null);
  const [isEmployee, setIsEmployee] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    activeHookConsumers += 1;

    async function init() {
      try {
        const streamState = await initializeSharedClient();

        if (!isMounted) {
          return;
        }
        setClient(streamState.client);
        setStreamUser(streamState.streamUser);
        setIsEmployee(streamState.isEmployee);
      } catch (err) {
        if (!isMounted) {
          return;
        }
        setError(
          err?.response?.data?.error ||
            err?.message ||
            "Failed to initialize messaging."
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    init();

    return () => {
      isMounted = false;
      activeHookConsumers = Math.max(0, activeHookConsumers - 1);
      if (sharedClient && sharedUserId) {
        scheduleSharedDisconnectIfUnused();
      }
    };
  }, []);

  return { client, streamUser, isEmployee, loading, error };
}
